import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-foreground-400 w-8 h-8 flex items-center justify-center"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您要查看的漫画不存在或已被移除</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catInfo = categories.find((c) => c.slug === comic.category);
  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.tags.some((t) => comic.tags.includes(t))))
    .slice(0, 6);

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toLocaleString();
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `${comic.title} - 韩漫在线阅读`,
        description: comic.description,
        author: { '@type': 'Person', name: comic.author },
        datePublished: comic.updatedAt,
        image: comic.coverImage,
        url: typeof window !== 'undefined' ? window.location.href : '',
        publisher: {
          '@type': 'Organization',
          name: '韩漫网站',
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin + '/' : '/' },
          ...(catInfo
            ? [{ '@type': 'ListItem', position: 2, name: `${catInfo.name}韩漫`, item: typeof window !== 'undefined' ? `${window.location.origin}/category/${catInfo.slug}` : '' }]
            : []),
          { '@type': 'ListItem', position: catInfo ? 3 : 2, name: comic.title },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200">
          <div className="px-4 md:px-6 max-w-6xl mx-auto py-4">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs w-4 h-4 flex items-center justify-center"></i>
              {catInfo && (
                <>
                  <Link to={`/category/${catInfo.slug}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">{catInfo.name}韩漫</Link>
                  <i className="ri-arrow-right-s-line text-xs w-4 h-4 flex items-center justify-center"></i>
                </>
              )}
              <span className="text-foreground-800 font-medium">{comic.title}</span>
            </nav>
          </div>
        </div>

        {/* Comic Info */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Cover */}
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-48 md:w-56 rounded-xl overflow-hidden shadow-sm">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-full aspect-[3/4] object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">{comic.title}</h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="flex items-center gap-1 text-sm text-amber-500">
                    <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i>
                    {comic.rating}
                  </span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600">{formatViews(comic.views)} 阅读</span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600">共 {comic.chapters} 话</span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  {catInfo && (
                    <Link
                      to={`/category/${catInfo.slug}`}
                      className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full hover:bg-accent-200 transition-colors whitespace-nowrap"
                    >
                      {catInfo.name}
                    </Link>
                  )}
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full">{tag}</span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-xl">{comic.description}</p>

                <div className="flex flex-wrap gap-3">
                  <button className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line w-4 h-4 flex items-center justify-center"></i>
                    开始阅读
                  </button>
                  <button className="px-8 py-3 bg-background-100 text-foreground-700 rounded-full font-medium text-sm hover:bg-background-200 transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line w-4 h-4 flex items-center justify-center"></i>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="py-8 md:py-12 bg-background-100">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">章节列表</h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 text-xs font-medium text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">正序</button>
                <span className="text-xs text-foreground-300">|</span>
                <button className="px-3 py-1.5 text-xs font-medium text-primary-500 whitespace-nowrap cursor-pointer">倒序</button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {comic.chapters_list.map((ch) => (
                <Link
                  key={ch.num}
                  to={`/comic/${comic.slug}`}
                  className="flex items-center justify-between px-4 py-3 bg-background-50 rounded-lg hover:bg-primary-50 transition-colors border border-background-200/60 cursor-pointer group"
                >
                  <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors">
                    第{ch.num}话 {ch.title}
                  </span>
                  <span className="text-[10px] text-foreground-400">{ch.date}</span>
                </Link>
              ))}
            </div>

            {comic.chapters > 5 && (
              <div className="flex justify-center mt-6 gap-2">
                {[1, 2, 3, '...', Math.ceil(comic.chapters / 5)].map((page, i) => (
                  <button
                    key={i}
                    className={`w-8 h-8 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                      page === 1
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="py-8 md:py-12 bg-background-50">
            <div className="px-4 md:px-6 max-w-6xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <Link
                    key={c.id}
                    to={`/comic/${c.slug}`}
                    className="group block cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                      <img
                        src={c.coverImage}
                        alt={c.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          c.status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-secondary-500 text-background-50'
                        }`}>
                          {c.status}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">{c.title}</h3>
                    <p className="text-xs text-foreground-500 mt-1">{c.author}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}